import { useTranslation } from 'react-i18next';
import type { DiffEntry } from '../types/changeHistoryViewer';

interface FieldDiffViewProps {
  changes: DiffEntry[];
}

function getDiffKind(entry: DiffEntry): 'added' | 'removed' | 'modified' {
  if (entry.before === null || entry.before === undefined) return 'added';
  if (entry.after === null || entry.after === undefined) return 'removed';
  return 'modified';
}

function formatValue(val: unknown): string {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}

/**
 * FieldDiffView — before/after 필드 diff 시각화
 * added=초록 / removed=빨강 / modified=노랑 하이라이트
 */
export function FieldDiffView({ changes }: FieldDiffViewProps) {
  const { t } = useTranslation();

  if (changes.length === 0) {
    return (
      <p
        style={{
          color: 'var(--color-text-secondary)',
          fontSize: 'var(--font-size-sm)',
          textAlign: 'center',
          padding: 'var(--spacing-4)',
        }}
      >
        {t('changeHistoryViewer.diff.noChanges')}
      </p>
    );
  }

  return (
    <div role="list" aria-label={t('changeHistoryViewer.diff.title')}>
      {/* 헤더 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: 'var(--spacing-2)',
          padding: 'var(--spacing-2) var(--spacing-3)',
          background: 'var(--color-surface-muted)',
          borderBottom: '1px solid var(--color-surface-border)',
          fontSize: 'var(--font-size-xs)',
          fontWeight: 600,
          color: 'var(--color-text-secondary)',
        }}
      >
        <span>{t('changeHistoryViewer.diff.field')}</span>
        <span>{t('changeHistoryViewer.diff.before')}</span>
        <span>{t('changeHistoryViewer.diff.after')}</span>
      </div>

      {changes.map((entry) => {
        const kind = getDiffKind(entry);
        const rowBg =
          kind === 'added'
            ? 'var(--color-success-subtle, rgba(22,163,74,0.08))'
            : kind === 'removed'
            ? 'var(--color-danger-subtle, rgba(220,38,38,0.08))'
            : 'var(--color-warning-subtle, rgba(217,119,6,0.08))';
        const kindLabel = t(`changeHistoryViewer.diff.${kind}`);

        return (
          <div
            key={entry.path}
            role="listitem"
            aria-label={`${entry.path}: ${kindLabel}`}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 'var(--spacing-2)',
              padding: 'var(--spacing-2) var(--spacing-3)',
              borderBottom: '1px solid var(--color-surface-border)',
              background: rowBg,
              fontSize: 'var(--font-size-sm)',
              alignItems: 'start',
            }}
          >
            {/* 필드명 + 유형 배지 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span
                style={{
                  fontFamily: 'var(--font-family-mono)',
                  fontWeight: 600,
                  color: 'var(--color-text-primary)',
                }}
              >
                {entry.path}
              </span>
              <span
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color:
                    kind === 'added'
                      ? 'var(--color-success, #16a34a)'
                      : kind === 'removed'
                      ? 'var(--color-danger, #dc2626)'
                      : 'var(--color-warning, #d97706)',
                  fontWeight: 500,
                }}
              >
                {kindLabel}
              </span>
            </div>

            {/* before */}
            <div
              style={{
                fontFamily: 'var(--font-family-mono)',
                fontSize: 'var(--font-size-xs)',
                color:
                  kind === 'added'
                    ? 'var(--color-text-muted)'
                    : 'var(--color-danger, #dc2626)',
                wordBreak: 'break-all',
                padding: 'var(--spacing-1)',
                background:
                  kind !== 'added'
                    ? 'var(--color-danger-subtle, rgba(220,38,38,0.06))'
                    : 'transparent',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              {formatValue(entry.before)}
            </div>

            {/* after */}
            <div
              style={{
                fontFamily: 'var(--font-family-mono)',
                fontSize: 'var(--font-size-xs)',
                color:
                  kind === 'removed'
                    ? 'var(--color-text-muted)'
                    : 'var(--color-success, #16a34a)',
                wordBreak: 'break-all',
                padding: 'var(--spacing-1)',
                background:
                  kind !== 'removed'
                    ? 'var(--color-success-subtle, rgba(22,163,74,0.06))'
                    : 'transparent',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              {formatValue(entry.after)}
            </div>
          </div>
        );
      })}

      {/* 요약 */}
      <div
        style={{
          padding: 'var(--spacing-2) var(--spacing-3)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-secondary)',
          background: 'var(--color-surface-muted)',
        }}
      >
        {t('changeHistoryViewer.diff.changesCount', { count: changes.length })}
      </div>
    </div>
  );
}
